import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Compass, ArrowRight, AlertCircle } from "lucide-react";

export default function LoginPage() {
    const navigate = useNavigate();
    const { login, formatApiError } = useAuth();
    const [email, setEmail] = useState("admin@protravel.ch");
    const [password, setPassword] = useState("ProTravel2026!");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            await login(email, password);
            navigate("/dashboard");
        } catch (ex) {
            setErr(formatApiError(ex));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[5fr_7fr] bg-sand">
            {/* Left: Form */}
            <div className="flex flex-col p-8 lg:p-14">
                <div className="flex items-center gap-2.5 mb-16">
                    <div className="w-9 h-9 rounded-md bg-navy flex items-center justify-center">
                        <Compass className="w-5 h-5 text-sand" strokeWidth={1.75} />
                    </div>
                    <div className="leading-none">
                        <div className="text-[15px] font-bold tracking-tight text-ink" style={{ fontFamily: "Space Grotesk" }}>Pro-Travel</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-ink-tertiary mt-1">Planner</div>
                    </div>
                </div>

                <div className="flex-1 flex flex-col justify-center max-w-md">
                    <p className="pt-eyebrow mb-4">Login · Reiseberatung</p>
                    <h1 className="pt-h1 mb-3" data-testid="login-heading">
                        Wieder im <span className="italic font-normal" style={{ fontFamily: "Fraunces" }}>Atelier</span>.
                    </h1>
                    <p className="pt-body mb-10">
                        Massgeschneiderte Ägypten-Reisen — präzise kalkuliert, hochwertig präsentiert.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">E-Mail</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                className="w-full bg-white border border-sand-300 rounded-md px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-navy focus:border-transparent transition-shadow"
                                placeholder="berater@agentur.ch"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Passwort</label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                className="w-full bg-white border border-sand-300 rounded-md px-4 py-3 text-ink focus:outline-none focus:ring-2 focus:ring-navy focus:border-transparent transition-shadow"
                                placeholder="••••••••••"
                            />
                        </div>

                        {err && (
                            <div className="flex items-start gap-2 p-3 bg-terracotta/10 border border-terracotta/30 rounded-md" data-testid="login-error">
                                <AlertCircle className="w-4 h-4 text-terracotta mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-terracotta-hover">{err}</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="login-submit-button"
                            className="group w-full bg-navy text-sand hover:bg-navy-hover rounded-md px-6 py-3.5 font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {busy ? "Anmeldung läuft…" : "Einloggen"}
                            {!busy && <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />}
                        </button>
                    </form>

                    <p className="mt-8 text-sm text-ink-secondary">
                        Noch kein Konto?{" "}
                        <Link to="/register" className="text-terracotta hover:text-terracotta-hover font-semibold underline-offset-4 hover:underline" data-testid="link-to-register">
                            Berater-Konto erstellen
                        </Link>
                    </p>
                </div>

                <div className="text-xs text-ink-tertiary mt-10">
                    © 2026 Pro-Travel Planner · Swiss-engineered für Reiseberater.
                </div>
            </div>

            {/* Right: Visual */}
            <div className="hidden lg:block relative grain overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1667555125597-462812b8fb2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxFZ3lwdCUyMHJlc29ydCUyMHBvb2x8ZW58MHx8fHwxNzc4MjMwNTgxfDA&ixlib=rb-4.1.0&q=85"
                    alt="Egypt resort"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-navy/70 via-navy/20 to-transparent" />
                <div className="absolute bottom-12 left-12 right-12 text-sand">
                    <p className="pt-eyebrow text-sand-300 mb-4" style={{ color: "#CFC8BB" }}>Hurghada · Marsa Alam · Kairo</p>
                    <h2 className="text-4xl font-bold leading-tight max-w-md" style={{ fontFamily: "Space Grotesk" }}>
                        Geniesse deine <span style={{ fontFamily: "Fraunces", fontStyle: "italic", fontWeight: 400 }}>Traumferien</span> am Roten Meer.
                    </h2>
                </div>
            </div>
        </div>
    );
}
